import { z } from 'zod';
import { optionalText } from '../dto/_fields.js';
import {
  brachycephalySeverities,
  pediatricAbdominalPalpations,
  pediatricAlertSigns,
  pediatricAnesthesias,
  pediatricAxialTones,
  pediatricBehaviors,
  pediatricBirthComplications,
  pediatricCranialSymmetries,
  pediatricDeliveryTypes,
  pediatricDiaphragmMobilities,
  pediatricFamilyConditions,
  pediatricFeedingRegurgitations,
  pediatricFeedingTypes,
  pediatricFirstWeekSimpleEvents,
  pediatricFontanelleStates,
  pediatricGlobalTones,
  pediatricInitialFeedingTypes,
  pediatricLimbTones,
  pediatricMilestones,
  pediatricNeonatalFeedingTypes,
  pediatricNeonatalSigns,
  pediatricOxygenTypes,
  pediatricPregnancyComplications,
  pediatricPregnancyEvents,
  pediatricRecentEvents,
  pediatricReferralSources,
  pediatricReferralTargets,
  pediatricReferralUrgencies,
  pediatricReflexes,
  pediatricReflexStates,
  pediatricRotationPreferences,
  pediatricSides,
  pediatricSleepLocations,
  pediatricSleepQualities,
  pediatricSocialSigns,
  pediatricSpineAlignments,
  pediatricStoolColors,
  pediatricStoolConsistencies,
  pediatricSutureStates,
  pediatricSymptomOnsets,
  pediatricSymptoms,
  pediatricTerms,
  pediatricTransfusionTypes,
  pediatricVisitRegurgitations,
  plagiocephalySeverities,
} from './options.js';

/**
 * Contenu du bilan pédiatrique, en deux blocs qui ne vivent pas au même rythme :
 *
 * — `history` : modules A (données statiques) et B (développement, alimentation). Il se **reprend**
 *   d'une consultation à la suivante — c'est ce que la V1 appelait le profil pédiatrique ;
 * — `visit` : module C, les observations du jour. Il ne se reprend **jamais**.
 *
 * Schéma strict écrit à la main : une clé inconnue est refusée, pas ignorée. La V1 déclarait le
 * même contenu en trois copies (type de colonne, schéma de formulaire, tables d'éclatement) qui
 * avaient divergé ; ici il n'y en a qu'une, lue par l'écran et par le serveur.
 *
 * Les mesures sont des nombres avec leur plage de vraisemblance — la V1 les gardait en texte
 * libre (« 3.2 », « 12 kg »). Les durées que la V1 laissait au praticien avec leur unité
 * (« 48 heures », « 3 jours ») restent du texte : les forcer en nombre perdrait l'unité.
 */

/** Plages de vraisemblance, une seule fois : le schéma les impose, l'écran les affiche. */
export const pediatricRanges = {
  numberOfChildren: { min: 0, max: 30, integer: true },
  parentAgeAtBirth: { min: 10, max: 90, integer: true },
  weightGainKg: { min: -30, max: 60, integer: false },
  laborDurationHours: { min: 0, max: 100, integer: false },
  birthWeightKg: { min: 0.3, max: 7, integer: false },
  birthLengthCm: { min: 20, max: 70, integer: false },
  headCircumferenceCm: { min: 15, max: 50, integer: false },
  apgar: { min: 0, max: 10, integer: true },
  dischargeWeightG: { min: 300, max: 10_000, integer: true },
  milestoneAgeMonths: { min: 0, max: 36, integer: false },
  currentWeightKg: { min: 0.5, max: 25, integer: false },
  cranialMm: { min: 50, max: 300, integer: false },
} as const;
export type PediatricRangeKey = keyof typeof pediatricRanges;

const SHORT = 200;
const LONG = 2000;

const shortText = optionalText(SHORT).optional();
const longText = optionalText(LONG).optional();
const flag = z.boolean().optional();

function measure(key: PediatricRangeKey) {
  const range = pediatricRanges[key];
  const base = z.number().min(range.min).max(range.max);
  return (range.integer ? base.int() : base).optional();
}

/** Un groupe de cases : seules les clés de la liste sont admises. */
function flags<const K extends readonly [string, ...string[]]>(keys: K) {
  return z.partialRecord(z.enum(keys), z.boolean()).optional();
}

function choice<const K extends readonly [string, ...string[]]>(values: K) {
  return z.enum(values).optional();
}

/* Module A --------------------------------------------------------------- */

const parentalHistorySchema = z
  .object({
    numberOfChildren: measure('numberOfChildren'),
    familyHistory: flags(pediatricFamilyConditions),
    otherConditions: longText,
  })
  .strict();

const pregnancyHistorySchema = z
  .object({
    /** Dossiers des parents dans le cabinet. Le serveur vérifie qu'ils existent et y sont visibles. */
    motherPatientId: z.uuid().optional(),
    fatherPatientId: z.uuid().optional(),
    motherAgeAtBirth: measure('parentAgeAtBirth'),
    fatherAgeAtBirth: measure('parentAgeAtBirth'),
    complications: flags(pediatricPregnancyComplications),
    infectionsDetails: longText,
    events: flags(pediatricPregnancyEvents),
    weightGainKg: measure('weightGainKg'),
    tobacco: flag,
    tobaccoAmount: shortText,
    alcohol: flag,
    alcoholAmount: shortText,
    drugs: flag,
    drugsType: shortText,
    medications: longText,
  })
  .strict();

const birthHistorySchema = z
  .object({
    deliveryType: choice(pediatricDeliveryTypes),
    cesareanReason: longText,
    term: choice(pediatricTerms),
    termDetails: shortText,
    laborDurationHours: measure('laborDurationHours'),
    anesthesia: choice(pediatricAnesthesias),
    complications: flags(pediatricBirthComplications),
    accelerators: flag,
    babyPresentation: shortText,
  })
  .strict();

const neonatalExamSchema = z
  .object({
    premature: flag,
    birthWeightKg: measure('birthWeightKg'),
    birthLengthCm: measure('birthLengthCm'),
    headCircumferenceCm: measure('headCircumferenceCm'),
    apgar1: measure('apgar'),
    apgar3: measure('apgar'),
    apgar5: measure('apgar'),
    apgar10: measure('apgar'),
    feedingType: choice(pediatricNeonatalFeedingTypes),
    signs: flags(pediatricNeonatalSigns),
    dischargeAge: shortText,
    dischargeWeightG: measure('dischargeWeightG'),
    remarks: longText,
  })
  .strict();

const firstWeekSchema = z
  .object({
    feedingTrouble: flag,
    feedingTroubleDetails: shortText,
    excessVomiting: flag,
    excessVomitingDetails: shortText,
    events: flags(pediatricFirstWeekSimpleEvents),
    jaundice: flag,
    phototherapyDuration: shortText,
    seizures: flag,
    seizuresType: shortText,
    seizuresTreatment: shortText,
    oxygen: flag,
    oxygenType: choice(pediatricOxygenTypes),
    oxygenDuration: shortText,
    transfusion: flag,
    transfusionType: choice(pediatricTransfusionTypes),
    transfusionReason: shortText,
    antibiotics: flag,
    antibioticsDuration: shortText,
    antibioticsIndication: shortText,
    intensiveCare: flag,
    intensiveCareDuration: shortText,
    intensiveCareReason: shortText,
  })
  .strict();

/* Module B --------------------------------------------------------------- */

const milestoneSchema = z
  .object({ achieved: flag, ageMonths: measure('milestoneAgeMonths') })
  .strict();

const psychomotorSchema = z
  .object({
    milestones: z.partialRecord(z.enum(pediatricMilestones), milestoneSchema).optional(),
    social: flags(pediatricSocialSigns),
  })
  .strict();

export const PEDIATRIC_FEEDING_HISTORY_MAX = 50;

const feedingHistoryEntrySchema = z
  .object({
    /** Clé stable de la ligne, posée par l'écran : elle ne désigne rien d'autre. */
    id: z.string().min(1).max(64),
    date: z.iso.date().optional(),
    type: z.enum(pediatricFeedingTypes),
    notes: optionalText(500).optional(),
  })
  .strict();

const feedingSchema = z
  .object({
    current: z
      .object({
        type: choice(pediatricFeedingTypes),
        frequency: shortText,
        duration: shortText,
        regurgitation: choice(pediatricFeedingRegurgitations),
        difficulties: flag,
        difficultiesDetails: longText,
      })
      .strict()
      .optional(),
    history: z.array(feedingHistoryEntrySchema).max(PEDIATRIC_FEEDING_HISTORY_MAX).optional(),
    initialType: choice(pediatricInitialFeedingTypes),
    breastfeedingDuration: shortText,
    breastfeedingSymptoms: longText,
    diversificationAge: shortText,
    problems: longText,
    intolerances: longText,
    allergies: longText,
  })
  .strict();

export const pediatricHistorySchema = z
  .object({
    parentalHistory: parentalHistorySchema.optional(),
    pregnancyHistory: pregnancyHistorySchema.optional(),
    birthHistory: birthHistorySchema.optional(),
    neonatalExam: neonatalExamSchema.optional(),
    firstWeek: firstWeekSchema.optional(),
    psychomotor: psychomotorSchema.optional(),
    feeding: feedingSchema.optional(),
  })
  .strict();
export type PediatricHistory = z.infer<typeof pediatricHistorySchema>;

/* Module C --------------------------------------------------------------- */

const visitReasonSchema = z
  .object({
    symptoms: flags(pediatricSymptoms),
    symptomDetails: longText,
    symptomDuration: shortText,
    symptomOnset: choice(pediatricSymptomOnsets),
    recentEvents: flags(pediatricRecentEvents),
    otherRecentEvents: longText,
    referralSource: choice(pediatricReferralSources),
    parentalConcerns: longText,
  })
  .strict();

const currentStateSchema = z
  .object({
    behavior: choice(pediatricBehaviors),
    behaviorNotes: shortText,
    sleepQuality: choice(pediatricSleepQualities),
    sleepHoursPerDay: shortText,
    nightWakings: shortText,
    sleepLocation: choice(pediatricSleepLocations),
    feedingType: choice(pediatricFeedingTypes),
    feedingFrequency: shortText,
    feedingDuration: shortText,
    regurgitations: choice(pediatricVisitRegurgitations),
    feedingDifficulties: flag,
    feedingDifficultiesDetails: longText,
    stoolFrequency: shortText,
    stoolConsistency: choice(pediatricStoolConsistencies),
    stoolColor: choice(pediatricStoolColors),
    gasProblems: flag,
    currentWeightKg: measure('currentWeightKg'),
    weightGainConcern: flag,
  })
  .strict();

/** Le CVAI et l'indice céphalique ne sont pas stockés : ils se déduisent des mesures. */
const cranialSchema = z
  .object({
    anteriorFontanelle: choice(pediatricFontanelleStates),
    posteriorFontanelle: choice(pediatricFontanelleStates),
    sutures: choice(pediatricSutureStates),
    symmetry: choice(pediatricCranialSymmetries),
    plagiocephaly: flag,
    plagiocephalySide: choice(pediatricSides),
    diagonalRightMm: measure('cranialMm'),
    diagonalLeftMm: measure('cranialMm'),
    plagiocephalySeverity: choice(plagiocephalySeverities),
    brachycephaly: flag,
    widthMm: measure('cranialMm'),
    lengthMm: measure('cranialMm'),
    brachycephalySeverity: choice(brachycephalySeverities),
    notes: longText,
  })
  .strict();

const clinicalExamSchema = z
  .object({
    cranial: cranialSchema.optional(),
    postural: z
      .object({
        torticollis: flag,
        torticollisSide: choice(pediatricSides),
        rotationPreference: choice(pediatricRotationPreferences),
        spineAlignment: choice(pediatricSpineAlignments),
        asymmetry: flag,
        notes: longText,
      })
      .strict()
      .optional(),
    digestive: z
      .object({
        palpation: choice(pediatricAbdominalPalpations),
        diaphragmMobility: choice(pediatricDiaphragmMobilities),
        distension: flag,
        periumbilicalTension: flag,
        notes: longText,
      })
      .strict()
      .optional(),
    tone: z
      .object({
        global: choice(pediatricGlobalTones),
        axial: choice(pediatricAxialTones),
        limbs: choice(pediatricLimbTones),
        reflexes: z
          .partialRecord(z.enum(pediatricReflexes), z.enum(pediatricReflexStates))
          .optional(),
        notes: longText,
      })
      .strict()
      .optional(),
    generalObservations: longText,
  })
  .strict();

const redFlagsSchema = z
  .object({
    alertSigns: flags(pediatricAlertSigns),
    otherAlertSign: longText,
    referralRecommended: flag,
    referralUrgency: choice(pediatricReferralUrgencies),
    referralTo: choice(pediatricReferralTargets),
    referralReason: longText,
    communicationWithDoctor: flag,
    communicationNotes: longText,
  })
  .strict();

export const pediatricVisitSchema = z
  .object({
    visitReason: visitReasonSchema.optional(),
    currentState: currentStateSchema.optional(),
    clinicalExam: clinicalExamSchema.optional(),
    redFlags: redFlagsSchema.optional(),
  })
  .strict();
export type PediatricVisit = z.infer<typeof pediatricVisitSchema>;
